import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'antd';

import { isActionKind } from '../../tours/tourSchema';
import { controlWithin, focusStops, hasValue, isPinnedToScreen, prefersReducedMotion, routeMatches } from './targets';
import { inflate, placeCard } from './placement';
import {
	TARGET_WAIT_MS,
	TYPE_SETTLE_MS,
	useDocked,
	useElementSize,
	useTargetElement,
	usePathname,
	useViewportRect,
	useViewportSize,
} from './tourHooks';
import useTourRunner from './useTourRunner';

/** Space around the cutout, and how far from a screen edge the card stays. */
const RING_PAD = 6;
const SCROLL_CLEARANCE_TOP = 80; // the pinned shell header
const SCROLL_CLEARANCE = 24;

const HINTS = {
	click: 'Click the highlighted item to continue.',
	type: 'Enter a value in the highlighted field to continue.',
	navigate: 'Go to the next page to continue.',
};

/**
 * THE TOUR ENGINE'S SURFACE (KPR-611): the spotlight over a dimmed page, the
 * step card, and the watch for what the person does. The step logic is
 * `useTourRunner`; the definition is a `loadTour` result; `TourProvider`
 * starts one from anywhere. Rules: docs/platform-standards.md § 16.
 *
 * Nothing is simulated. On a `click`, `type` or `navigate` step the target
 * stays usable (the dimmed page around it is not) and the tour advances when
 * the person really clicks it, really holds a value in it, or really reaches
 * the route. The engine reads the page and never writes to it, except for a
 * scroll margin it puts on the target for the length of one scroll.
 *
 * @param {object} props
 * @param {Readonly<import('../../tours/tourSchema').Tour>} props.tour
 * @param {object} [props.context]         handed to every step condition
 * @param {{ element: Element|null, active: boolean }} [props.focusReturn]
 *        where focus goes back to on exit (default: where it was on mount)
 * @param {{ targetWaitMs?: number, typeSettleMs?: number }} [props.timing]
 * @param {Function} [props.onStep] [props.onComplete] [props.onDismiss] [props.onExit]
 */
export default function TourRunner({ tour, context, focusReturn, timing, onStep, onComplete, onDismiss, onExit }) {
	const targetWaitMs = (timing && timing.targetWaitMs) ?? TARGET_WAIT_MS;
	const typeSettleMs = (timing && timing.typeSettleMs) ?? TYPE_SETTLE_MS;
	const pathname = usePathname();
	const docked = useDocked();
	const viewport = useViewportSize();
	const titleId = useId();
	const textId = useId();

	// Where focus goes back to. Read before anything moves it.
	const origin = useRef(focusReturn || { element: null, active: true });
	useLayoutEffect(() => {
		if (!origin.current.element && typeof document !== 'undefined') origin.current.element = document.activeElement;
	}, []);
	const restoreFocus = () => {
		const { element, active } = origin.current;
		if (!active || !element || !element.isConnected || typeof element.focus !== 'function') return;
		element.focus({ preventScroll: true });
	};

	const runner = useTourRunner({ tour, context, pathname, onStep, onComplete, onDismiss, onExit, onEnded: restoreFocus });
	const { step, stepKey, position, total, canBack, isLast, direction } = runner;
	const kind = step ? step.kind : 'info';
	const action = isActionKind(kind);

	const { element, missing } = useTargetElement(step ? step.target : null, stepKey, targetWaitMs);
	const hasTarget = Boolean(step && step.target);
	const targetRect = useViewportRect(element);

	// A target that is not coming: skip the step.
	useEffect(() => {
		if (step && step.target && missing) runner.skipCurrent('missing', stepKey);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [missing, stepKey]);

	// A field that already holds a value has nothing to wait for.
	useEffect(() => {
		if (!step || step.kind !== 'type' || !element || direction !== 1) return;
		if (hasValue(controlWithin(element))) runner.skipCurrent('satisfied', stepKey);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [element, stepKey]);

	// The person's action, watched for and never performed.
	useEffect(() => {
		if (!step || !isActionKind(step.kind)) return undefined;
		const advance = (delay = 0) => setTimeout(() => runner.next(stepKey), delay);
		if (step.kind === 'click') {
			if (!element) return undefined;
			let timer = null;
			const onClick = (event) => {
				if (element.contains(event.target) && timer === null) timer = advance();
			};
			document.addEventListener('click', onClick, true);
			return () => {
				document.removeEventListener('click', onClick, true);
				clearTimeout(timer);
			};
		}
		if (step.kind === 'type') {
			const control = controlWithin(element);
			if (!control) return undefined;
			let timer = null;
			const clear = () => {
				clearTimeout(timer);
				timer = null;
			};
			const onInput = () => {
				clear();
				if (hasValue(control)) timer = advance(typeSettleMs);
			};
			const onBlur = () => {
				if (!hasValue(control)) return;
				clear();
				timer = advance();
			};
			const onKey = (event) => {
				if (event.key === 'Enter' && control.tagName === 'INPUT' && hasValue(control)) {
					clear();
					timer = advance();
				}
			};
			control.addEventListener('input', onInput);
			control.addEventListener('change', onInput);
			control.addEventListener('blur', onBlur);
			control.addEventListener('keydown', onKey);
			return () => {
				clear();
				control.removeEventListener('input', onInput);
				control.removeEventListener('change', onInput);
				control.removeEventListener('blur', onBlur);
				control.removeEventListener('keydown', onKey);
			};
		}
		return undefined;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [element, stepKey, typeSettleMs]);

	// A navigate step waits for the route to become the one it names.
	const pathAtEntry = useRef(null);
	useEffect(() => {
		pathAtEntry.current = pathname;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stepKey]);
	useEffect(() => {
		if (!step || step.kind !== 'navigate') return undefined;
		if (pathname === pathAtEntry.current || !routeMatches(step.route, pathname)) return undefined;
		const timer = setTimeout(() => runner.next(stepKey), 0);
		return () => clearTimeout(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname, stepKey]);

	// Esc leaves, wherever focus is and whatever else is open. Captured at the
	// document so it does not also close the modal the step may be pointing
	// into, or cancel the form behind it.
	const cardRef = useRef(null);
	useEffect(() => {
		const onKeyDown = (event) => {
			if (event.isComposing || event.keyCode === 229) return;
			if (event.key === 'Escape') {
				event.preventDefault();
				event.stopImmediatePropagation();
				runner.dismiss('escape');
				return;
			}
			if (event.key === 'Tab') trapTab(event, cardRef.current, action ? element : null);
		};
		document.addEventListener('keydown', onKeyDown, true);
		return () => document.removeEventListener('keydown', onKeyDown, true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [element, action]);

	// Geometry: where the card sits, and keeping the target above it.
	const cardSize = useElementSize(cardRef, `${stepKey}:${Boolean(element && targetRect) || !hasTarget}`);
	const ringRect = targetRect && element ? inflate(targetRect, RING_PAD, viewport) : null;
	const placed = ringRect && !docked ? placeCard(ringRect, cardSize, viewport) : null;
	// On a phone the card sits at the bottom, which would cover a target that is
	// pinned to the bottom of the screen (the tab bar); for one in the lower half
	// it docks at the top instead.
	const pinned = useMemo(() => (element ? isPinnedToScreen(element) : false), [element]);
	const dockedTop = Boolean(docked && pinned && targetRect && targetRect.top + targetRect.height / 2 > viewport.height / 2);

	const cardBucket = Math.round(cardSize.height / 8);
	useEffect(() => {
		if (!element || typeof element.scrollIntoView !== 'function') return;
		const before = [element.style.scrollMarginTop, element.style.scrollMarginBottom];
		element.style.scrollMarginTop = `${SCROLL_CLEARANCE_TOP}px`;
		element.style.scrollMarginBottom = `${docked && !dockedTop ? cardSize.height + SCROLL_CLEARANCE : SCROLL_CLEARANCE}px`;
		element.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
		[element.style.scrollMarginTop, element.style.scrollMarginBottom] = before;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [element, docked, dockedTop, cardBucket]);

	// Focus and the words for a screen reader. The card takes focus when the
	// tour starts and whenever the button that was pressed is not there to
	// keep it (Back on the first step, an action the person did on the page).
	// When focus stays on a button, the live region says the new step instead.
	const [announcement, setAnnouncement] = useState('');
	useEffect(() => {
		if (!step || !cardRef.current) return;
		const card = cardRef.current;
		const active = document.activeElement;
		const keeps = active && card.contains(active) && active !== card && !active.disabled;
		if (!keeps) card.focus({ preventScroll: true });
		setAnnouncement(keeps ? `Step ${position} of ${total}. ${step.title}. ${step.text}` : '');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stepKey, hasTarget && !(element && targetRect)]);

	if (!step || runner.ended) return null;
	// A step with a target is drawn once the target is on the page. Until then
	// the card stays mounted but hidden, so the button that was pressed keeps
	// focus across the change of step.
	const pending = hasTarget && !(element && targetRect);

	const centred = !hasTarget;
	const hint = step.hint || HINTS[step.kind];
	const nextLabel = isLast ? 'Finish' : (action ? 'Skip step' : 'Next');
	const cardStyle = placed ? { top: placed.top, left: placed.left } : undefined;
	const className = [
		'tour__card',
		centred ? 'tour__card--centred' : '',
		docked ? 'tour__card--docked' : '',
		dockedTop ? 'tour__card--docked-top' : '',
		pending ? 'tour__card--pending' : '',
		placed ? `tour__card--${placed.side}` : '',
	].filter(Boolean).join(' ');

	return createPortal(
		<>
			<div className='tour__layer' data-tour-layer='' aria-hidden='true'>
				{centred && <div className='tour__dim' />}
				{!centred && !pending && ringRect && (
					<>
						<div className='tour__ring' style={ringStyle(ringRect)} />
						{action ? <Blockers rect={ringRect} viewport={viewport} /> : <div className='tour__blocker tour__blocker--all' />}
					</>
				)}
			</div>
			<div
				ref={cardRef}
				className={className}
				style={cardStyle}
				role='dialog'
				aria-modal='true'
				aria-labelledby={titleId}
				aria-describedby={textId}
				tabIndex={-1}
				data-tour-card=''
				data-step-kind={step.kind}
			>
				<div className='tour__count micro-label'>{`Step ${position} of ${total}`}</div>
				<h2 className='tour__title' id={titleId}>{step.title}</h2>
				<p className='tour__text' id={textId}>{step.text}</p>
				{action && hint && <p className='tour__hint'>{hint}</p>}
				<div className='tour__actions'>
					<Button type='text' className='tour__exit' onClick={() => runner.dismiss('exit-button')}>Exit tour</Button>
					<span className='tour__steps'>
						<Button onClick={runner.back} disabled={!canBack}>Back</Button>
						<Button type={action && !isLast ? 'default' : 'primary'} onClick={() => runner.next()}>{nextLabel}</Button>
					</span>
				</div>
			</div>
			<div className='tour__sr' role='status' aria-live='polite'>{announcement}</div>
		</>,
		document.body,
	);
}

const ringStyle = (rect) => ({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });

/** Four panes around the cutout: the page stays inert except through the hole. */
function Blockers({ rect, viewport }) {
	const bottom = rect.top + rect.height;
	const right = rect.left + rect.width;
	return (
		<>
			<div className='tour__blocker' style={{ top: 0, left: 0, width: viewport.width, height: rect.top }} />
			<div className='tour__blocker' style={{ top: bottom, left: 0, width: viewport.width, height: Math.max(0, viewport.height - bottom) }} />
			<div className='tour__blocker' style={{ top: rect.top, left: 0, width: rect.left, height: rect.height }} />
			<div className='tour__blocker' style={{ top: rect.top, left: right, width: Math.max(0, viewport.width - right), height: rect.height }} />
		</>
	);
}

/**
 * Tab stays inside the card, plus (on a step the person acts on) the target.
 * Focus somewhere else on purpose (a menu the click opened) is left alone.
 */
function trapTab(event, card, target) {
	if (!card) return;
	const stops = [...focusStops(card), ...(target ? focusStops(target) : [])];
	const active = document.activeElement;
	const inside = (node) => Boolean(node) && (card.contains(node) || (target && target.contains(node)));
	if (active && active !== document.body && active !== card && !inside(active)) return;
	event.preventDefault();
	if (stops.length === 0) {
		card.focus({ preventScroll: true });
		return;
	}
	const at = stops.indexOf(active);
	let to;
	if (at < 0) to = event.shiftKey ? stops.length - 1 : 0;
	else to = (at + (event.shiftKey ? -1 : 1) + stops.length) % stops.length;
	stops[to].focus({ preventScroll: true });
}
